// The casters of Godsmode, from Rob's brief in casters.md. A caster has four
// slots. A skill fills a slot for the match and costs energy. A scroll slot
// holds one scroll, which costs nothing and is used up. The names of the
// casters are Rob's. A skill and a scroll are spells, by mechanic id.

import type { DieFaceLabel } from '../dice/octahedron';
import type { SpellId } from '../spells/types';

export type CasterId = string;

export type CasterSkill = {
  spellId: SpellId;
  /** Energy. */
  cost: number;
  /** The skill unlocks when the army of the caster falls to half strength, and stays unlocked. */
  atHalfStrength?: true;
};

export type Caster = {
  id: CasterId;
  name: string;
  skills: readonly CasterSkill[];
  scrollSlots: number;
  /**
   * One line about the caster, for the pane of the select. It is card text, so
   * it is Rob's. A caster with none shows no line.
   */
  summary?: string;
};

export const CASTERS: readonly Caster[] = [
  {
    id: 'parch',
    name: 'Parch',
    skills: [{ spellId: 'new-scroll', cost: 2 }],
    scrollSlots: 3,
  },
  {
    id: 'marathone',
    name: 'Marathoné',
    skills: [
      { spellId: 'run-forward', cost: 2 },
      { spellId: 'add-knight-moves', cost: 3 },
      { spellId: 'checkers-energy', cost: 4, atHalfStrength: true },
    ],
    scrollSlots: 1,
  },
  {
    id: 'claude-opus',
    name: 'Cerebritron',
    skills: [
      // 1 and 3 since 2026-10-14. They were 2 and 4, and Cerebritron won 36% of dealt matches.
      { spellId: 'suggest-best', cost: 1 },
      { spellId: 'copy-abilities', cost: 3 },
      { spellId: 'piece-downgrade', cost: 4, atHalfStrength: true },
    ],
    scrollSlots: 1,
  },
  {
    id: 'sinistri',
    name: 'Sinistri',
    skills: [
      { spellId: 'kill-captor', cost: 2 },
      { spellId: 'explode-later', cost: 4 },
      { spellId: 'lost-energy', cost: 2, atHalfStrength: true },
    ],
    scrollSlots: 1,
  },
  {
    id: 'stabb',
    name: 'Stabb',
    skills: [
      { spellId: 'deadly-square', cost: 2 },
      { spellId: 'pawn-changes-side', cost: 3 },
    ],
    scrollSlots: 2,
  },
  {
    id: 'frenkelstone',
    name: 'Frenkelstone',
    skills: [
      { spellId: 'clone-square', cost: 3 },
      { spellId: 'clone-bud', cost: 4 },
      { spellId: 'extra-life', cost: 4, atHalfStrength: true },
    ],
    scrollSlots: 1,
  },
  {
    id: 'upmost',
    name: 'Upmost',
    skills: [
      { spellId: 'move-to-own-half', cost: 2 },
      { spellId: 'sacrifice-pawns', cost: 3 },
      { spellId: 'make-royal', cost: 4, atHalfStrength: true },
    ],
    scrollSlots: 1,
  },
  {
    id: 'stokaz',
    name: 'Stokaz',
    skills: [
      { spellId: 'enemy-lines', cost: 2 },
      { spellId: 'row-scramble', cost: 3 },
      { spellId: 'trade-squares', cost: 4, atHalfStrength: true },
    ],
    scrollSlots: 1,
  },
];

/** The spells a scroll can be. `defuse-bomb` is in the draw only while a fuse is lit. */
export const SCROLLS: readonly SpellId[] = [
  'change-kind',
  'defuse-bomb',
  'stop-piece',
  'push-pieces',
  'forced-peace',
];

/** What a face of the die gives at the start of a turn: energy, or a scroll. */
export type EnergyFace = number | 'scroll';
/**
 * Rob, 2026-10-12: "a new energy die economy that's 1-1-1-1-2-2-S-S". No face
 * gives nothing. It was 0, 0, 1, 1, 1, 1, 2, S at first, then 0, 1, 1, 1, 1,
 * 1, 2, S.
 */
export const ENERGY_DIE: readonly EnergyFace[] = [1, 1, 1, 1, 2, 2, 'scroll', 'scroll'];

/** What each face of the die reads in a match with a wheel: the number of the face. */
export const WHEEL_DIE_LABELS: readonly string[] = ENERGY_DIE.map((_, face) => String(face + 1));

/** What the scroll face of the die reads. A plain letter, not final. */
export const SCROLL_FACE_LABEL = 'S';

/**
 * What each face of the die reads in a match with casters. An energy face
 * has one coin for each energy. It had the number until 2026-10-12.
 */
export const ENERGY_DIE_LABELS: readonly DieFaceLabel[] = ENERGY_DIE.map((face) =>
  face === 'scroll' ? SCROLL_FACE_LABEL : { coins: face },
);

/**
 * The casters a side has in a match. Rob, 2026-10-12: "Now you pick TWO casters
 * at the start of a match, and you have both their spells to work with. Moon
 * also takes two." It was one.
 */
export const CASTERS_PER_SIDE = 2;

export const casterById = (id: CasterId): Caster => {
  const caster = CASTERS.find((candidate) => candidate.id === id);
  if (!caster) throw new Error(`Unknown caster "${id}"`);
  return caster;
};

/** A list of casters with no caster twice, and at least one. */
export const isCasterList = (value: unknown): value is CasterId[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(isCasterId) &&
  new Set(value).size === value.length;

export const isCasterId = (value: unknown): value is CasterId =>
  typeof value === 'string' && CASTERS.some((caster) => caster.id === value);
